import React, { useState, useContext } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import NtModalInputTitle from "../../components/NtModalInputTitle";
import NtToggleRow from "../../components/NtToggleRow";
import Logger from "../../common/utils/Logger";
import { parseError } from "@ntconnect/framework/pbx";
import NtModalError from "../../components/NtModalError";
import { createVoicemailBox } from "../../api/voicemail";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";

function NtVoicemailNewForm({ onCancel, onCreate }) {
  const [number, setNumber] = useState("");
  const [password, setPassword] = useState("");
  const [description, setDescription] = useState("");
  const [attachFileToEmail, setAttachFileToEmail] = useState(false);
  const [keepLocalAfterEmail, setKeepLocalAfterEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const context = useContext(UserContext);

  const isRequiredFields = () => number && password;

  const handleCreate = async () => {
    setLoading(true);
    setError(null);

    const params = {
      number,
      password,
      description,
      attachFileToEmail,
      keepLocalAfterEmail,
    };

    const response = await createVoicemailBox(
      context.user?.domain,
      params,
    );
    Logger("Voicemail box create", response);

    if (response.ok) {
      if (onCreate) onCreate();
    } else {
      setError(parseError(response));
    }
    setLoading(false);
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>Add new Voicemail Box</NtModalTitle>
      <NtModalSubtitle style={{ marginTop: 4 }}>
        Add a new voicemail box and assign it to an extension, ring group, etc.
      </NtModalSubtitle>

      <NtModalInputField
        containerStyle={{ marginTop: 25 }}
        title="Voicemail Number *"
        placeholder="Digits only — e.g. 5555"
        value={number}
        setValue={(text) => setNumber(text.replace(/\D/g, ""))}
        keyboardType="numeric"
      />

      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title="Password *"
        placeholder="Numeric PIN (digits only)"
        value={password}
        setValue={(text) => setPassword(text.replace(/\D/g, ""))}
        secureTextEntry={true}
        showEye={true}
        keyboardType="numeric"
      />

      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title="Notes"
        placeholder="i.e. Unified Voicemail for Front Desk"
        value={description}
        setValue={setDescription}
      />

      <NtModalInputTitle style={{ marginTop: 20, marginLeft: 4 }}>
        Email behavior
      </NtModalInputTitle>
      <View style={{ marginTop: 6 }}>
        <NtToggleRow
          value={attachFileToEmail}
          onChange={setAttachFileToEmail}
          label="Attach audio to email"
          sub="Include the voicemail .wav as an attachment on the notification email."
        />
        <View style={{ height: 8 }} />
        <NtToggleRow
          value={keepLocalAfterEmail}
          onChange={setKeepLocalAfterEmail}
          label="Keep local copy after email"
          sub="Keep the message in the voicemail box even after the email is sent."
        />
      </View>

      <NtModalError error={error} containerStyle={{ marginTop: 6 }} />

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          marginTop: 25,
        }}
      >
        {loading && (
          <NtActivityIndicator
            color={colors.blue}
            size="small"
            containerStyle={{ marginRight: 25 }}
          />
        )}

        <NtModalUnactiveButton title="Cancel" onPress={onCancel} />

        <NtModalActiveButton
          containerStyle={{ marginLeft: 15 }}
          title="Create"
          onPress={() => {
            if (isRequiredFields()) handleCreate();
          }}
          enabled={isRequiredFields()}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
});

export default NtVoicemailNewForm;
